<template>
  <v-input
    :error-messages="errors"
    :hide-details="dense"
    :rules="rules"
  >
    <div class="child-form-wrapper">
      <FormFieldSet
        v-if="props.field.multiple"
        :dense="dense"
        :label="field.label"
      >
        <div>
          <template
            v-for="(childObject, i) in innerValueList"
            :key="childObject._id"
          >
            <div class="child-form mb-2">
              <GenericFormLayoutChildObject
                dense
                :errors="{}"
                :fields="childFormFields"
                :model-value="childObject"
                :rules="innerRulesMap"
                @update:model-value="onChildUpdated(i, $event)"
              />

              <v-btn
                class="delete-btn"
                color="secondary"
                icon
                variant="text"
                @click="removeChildObject(i)"
              >
                <ViIcon icon="mdi:trash" />
              </v-btn>
            </div>
          </template>

          <v-btn
            v-if="field.multiple"
            block
            color="primary"
            variant="outlined"
            @click="addChildObject()"
          >
            <template #prepend>
              <vi-icon icon="mdi:plus" />
            </template>
            Add {{ field.label }}
          </v-btn>
        </div>
      </FormFieldSet>

      <div v-else>
        <v-label>
          <FieldLabel :field="field" />
        </v-label>

        <GenericFormLayoutChildObject
          dense
          :errors="{}"
          :fields="childFormFields"
          :model-value="innerValue"
          :rules="innerRulesMap"
          @update:model-value="$emit('update:modelValue', $event)"
        />
      </div>
    </div>
  </v-input>
</template>

<script setup lang="ts">
import { v4 as uuid4 } from 'uuid';

type ChildObjectList = (ChildObject & { _id: string })[];

// TODO: need to see if this can easily be used for single nested objects, rather than lists of them
const props = defineProps<{
  field: FieldConfig
  rules?: FormRule[]
  errors?: string[]
  dense?: boolean
  modelValue: ChildObject | ChildObjectList | undefined
}>();
const emit = defineEmits(['update:modelValue']);

const { rules: innerRulesMap } = useSetupFormFromFields({ fields: props.field.optionForm?.fields || [] });

const innerValue = ref((props.modelValue || {}) as ChildObject);
const innerValueList = ref(((props.modelValue || []) as ChildObjectList).map(o => ({ ...o, _id: uuid4() })));

watch(() => props.modelValue, () => {
  if (props.field.multiple) {
    innerValueList.value = props.modelValue as ChildObjectList;
  }
  else {
    innerValue.value = props.modelValue as ChildObject;
  }
}, { deep: true });
watch(innerValue, () => emit('update:modelValue', innerValue.value), { deep: true });

const childFormFields = computed(() => {
  return [
    {
      id: 'id',
      label: 'ID',
      type: 'text' as FieldType,
      hidden: true,
    },
    ...(props.field.optionForm?.fields || []),
  ];
});

const onChildUpdated = (index: number, data: { [key: string]: unknown }) => {
  const newValue = [...innerValueList.value];
  newValue[index] = { ...newValue[index], ...data };
  emit('update:modelValue', newValue);
};

const addChildObject = () => {
  emit(
    'update:modelValue',
    [...innerValueList.value, { _id: uuid4() }],
  );
};

const removeChildObject = (index: number) => {
  const newValue = [...innerValueList.value];
  newValue.splice(index, 1);
  emit('update:modelValue', newValue);
};
</script>

<style scoped>
.child-form-wrapper {
  width: 100%;
}

.child-form {
  display: grid;
  grid-template-columns: 1fr 3rem;
}
</style>
